import { prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';

const VIEWING_CLASS = 'gallery-grid--viewing';

class GalleryGrid extends HTMLElement {
  /** @type {HTMLElement | null} */
  list = null;

  /** @type {HTMLElement[]} */
  items = [];

  /** @type {HTMLButtonElement[]} */
  thumbs = [];

  /** @type {HTMLButtonElement | null} */
  prevButton = null;

  /** @type {HTMLButtonElement | null} */
  nextButton = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** The element focus returns to once the viewer closes. @type {HTMLElement | null} */
  #returnFocus = null;

  /** Index of the slide the viewer is showing. */
  #index = 0;

  /** Whether a programmatic scroll is still in flight. */
  #settling = false;

  constructor() {
    super();
    this.mediaQuery = window.matchMedia('(orientation: landscape)');
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOrientationChange = this.handleOrientationChange.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('.gallery-grid__list');
    this.items = Array.from(this.querySelectorAll('.gallery-grid__item'));
    this.thumbs = Array.from(this.querySelectorAll('.gallery-grid__thumb'));
    this.prevButton = this.querySelector('.gallery-grid__nav--prev');
    this.nextButton = this.querySelector('.gallery-grid__nav--next');

    if (!this.list || !this.items.length) return;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.list.addEventListener('pointerdown', this.#releaseSettling, { passive: true });
    this.list.addEventListener('wheel', this.#releaseSettling, { passive: true });
    this.mediaQuery.addEventListener('change', this.handleOrientationChange);

    const observer = new IntersectionObserver(this.#handleIntersection, { root: this.list, threshold: 0.5 });
    for (const item of this.items) observer.observe(item);
    this.#observer = observer;

    this.#syncThumbState();
    this.#updateNavState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.list?.removeEventListener('pointerdown', this.#releaseSettling);
    this.list?.removeEventListener('wheel', this.#releaseSettling);
    this.mediaQuery.removeEventListener('change', this.handleOrientationChange);
    this.#observer?.disconnect();
  }

  get isViewing() {
    return this.classList.contains(VIEWING_CLASS);
  }

  /**
   * Handles clicks on the thumbnails, the arrows and the back button.
   * @param {MouseEvent} event - The click event.
   */
  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.gallery-grid__back')) {
      this.close();
      return;
    }

    if (target.closest('.gallery-grid__nav--prev')) {
      this.#step(-1);
      return;
    }

    if (target.closest('.gallery-grid__nav--next')) {
      this.#step(1);
      return;
    }

    const thumb = target.closest('.gallery-grid__thumb');
    if (!(thumb instanceof HTMLElement) || this.isViewing || !this.mediaQuery.matches) return;

    this.open(Number(thumb.dataset.index) || 0);
  }

  /**
   * Handles keyboard navigation. Bound to the element rather than the document so that the
   * rest of the page, including the right hand column, keeps its own keyboard behaviour.
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeydown(event) {
    if (!this.isViewing) return;

    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.#step(-1);
        break;
      case 'ArrowRight':
        this.#step(1);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  handleOrientationChange() {
    if (!this.mediaQuery.matches) this.close();
    this.#syncThumbState();
  }

  /**
   * Opens the viewer on a given slide.
   * @param {number} index - The index of the image to show.
   */
  open(index) {
    if (this.isViewing) return;

    const trigger = document.activeElement;
    this.#returnFocus = trigger instanceof HTMLElement ? trigger : null;

    // Claim the flag before the layout switch, otherwise the observer reports slide 0
    // while the scroller is still at its initial position.
    this.#settling = true;
    this.classList.add(VIEWING_CLASS);
    this.focus({ preventScroll: true });
    this.#syncThumbState();
    this.#setIndex(index);

    requestAnimationFrame(() => this.#scrollToIndex('instant'));
  }

  close() {
    if (!this.isViewing) return;

    const activeItem = this.items[this.#index];
    const activeThumb = this.thumbs[this.#index];

    this.classList.remove(VIEWING_CLASS);
    this.#syncThumbState();

    if (activeItem) {
      scrollIntoView(activeItem, { ancestor: this, behavior: 'instant', block: 'center', inline: 'center' });
    }

    // Focus follows the image the viewer ended on, not the one that opened it.
    (activeThumb ?? this.#returnFocus)?.focus({ preventScroll: true });
    this.#returnFocus = null;
  }

  /**
   * Moves the viewer by a number of slides.
   * @param {number} delta - How many slides to move by.
   */
  #step(delta) {
    const nextIndex = Math.min(Math.max(this.#index + delta, 0), this.items.length - 1);
    if (nextIndex === this.#index) return;

    this.#setIndex(nextIndex);
    this.#scrollToIndex('smooth');
  }

  /**
   * Records the active slide and refreshes everything derived from it.
   * @param {number} index - The index of the active slide.
   */
  #setIndex(index) {
    this.#index = index;
    this.#updateNavState();
    if (this.isViewing) this.#upgradeImageSizes(index);
  }

  /**
   * Scrolls the viewer to the active slide.
   * @param {ScrollBehavior} behavior - The scroll behavior to request.
   */
  #scrollToIndex(behavior) {
    const item = this.items[this.#index];
    if (!item || !this.list) return;

    this.#settling = true;
    scrollIntoView(item, {
      ancestor: this.list,
      behavior: prefersReducedMotion() ? 'instant' : behavior,
      inline: 'start',
    });
  }

  #releaseSettling = () => {
    this.#settling = false;
  };

  /**
   * Keeps the active index in sync with manual swipes.
   * @param {IntersectionObserverEntry[]} entries - The observed slides.
   */
  #handleIntersection = (entries) => {
    if (!this.isViewing) return;

    const [mostVisible] = entries
      .filter((entry) => entry.intersectionRatio >= 0.5)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

    if (!mostVisible) return;

    const index = this.items.indexOf(/** @type {HTMLElement} */ (mostVisible.target));
    if (index === -1) return;

    if (this.#settling) {
      // Slides passing by mid-animation must not overwrite the requested index.
      if (index === this.#index) this.#settling = false;
      return;
    }

    this.#setIndex(index);
  };

  #syncThumbState() {
    const interactive = this.mediaQuery.matches && !this.isViewing;
    for (const thumb of this.thumbs) thumb.disabled = !interactive;
  }

  #updateNavState() {
    this.#setNavDisabled(this.prevButton, this.#index <= 0);
    this.#setNavDisabled(this.nextButton, this.#index >= this.items.length - 1);
  }

  /**
   * Toggles an arrow, moving focus away first so the keyboard does not get stranded.
   * @param {HTMLButtonElement | null} button - The arrow to toggle.
   * @param {boolean} disabled - Whether the arrow should be disabled.
   */
  #setNavDisabled(button, disabled) {
    if (!button || button.disabled === disabled) return;

    if (disabled && button.contains(document.activeElement)) this.focus({ preventScroll: true });

    button.disabled = disabled;
  }

  /**
   * Raises `sizes` on the slides about to be seen full width. The grid loads thumbnail-sized
   * candidates, so full resolution is only fetched once the viewer actually needs it.
   * @param {number} index - The index of the active slide.
   */
  #upgradeImageSizes(index) {
    for (let i = index - 1; i <= index + 1; i++) {
      const image = this.items[i]?.querySelector('img');
      const viewerSizes = this.thumbs[i]?.dataset.viewerSizes;
      if (!image || !viewerSizes || image.dataset.sizesUpgraded) continue;

      image.sizes = viewerSizes;
      image.dataset.sizesUpgraded = 'true';
    }
  }
}

if (!customElements.get('gallery-grid')) {
  customElements.define('gallery-grid', GalleryGrid);
}
